import React, { useCallback, useEffect, useState } from 'react';

import { TextField } from '@tmap/mmm-style-guide/src/TextField';
import { Autocomplete } from '@tmap/mmm-style-guide/src/Autocomplete';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { InputAdornment } from '@tmap/mmm-style-guide/src/Input';
import useDebounced from '@tmap/mmm-style-guide/src/useDebounced';

import useThunktank from '@tmap/mmm-core/redux/thunktank/useThunktank';
import { getUserProfileByEmailAutocomplete } from '../../../actions/userProfile';
import useCurrentUser from '../../../hooks/useCurrentUser';
import ProfileAvatar from '../../profileAvatar';

function AuthorAutocompleteField(props) {
  const { input } = props;
  const [inputValue, setInputValue] = useState('');

  const user = useCurrentUser();
  const useThunk = useThunktank('authorAutocomplete');
  const [state, getAutocomplete] = useThunk(getUserProfileByEmailAutocomplete);
  const autocomplete = state.data?.autocomplete || [];

  const fetchAutocomplete = useDebounced(getAutocomplete, 400);

  useEffect(() => {
    if (inputValue) fetchAutocomplete(inputValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const filledInput = Boolean(inputValue);

  // A 'no results found' message has limited usefulness here; once a selection is
  // made, we'll only ever have 0 or 1 results anyway based on the current value
  const noOptionsText = state.isLoading ? 'Loading...' : 'Search for users by email';

  const getDisplayName = useCallback((userProfile) => {
    if (Object.keys(userProfile).length === 0) return '';
    const label = (userProfile?.firstName || userProfile?.lastName)
      ? `${[userProfile.firstName, userProfile.lastName].join(' ').trim()}`
      : userProfile?.email?.trim();
    return userProfile._id === user._id ? `${label} (You)` : label;
  }, [user._id]);

  // this function is used for displaying the list of options; will include the profile photo.
  // the getDisplayName function is kept separate for showing the current selection, where
  // non-text content is not supported (we show the avatar via input adornment instead)
  const displayUser = useCallback((p, userProfile) => {
    const label = getDisplayName(userProfile);
    return (
      <li {...p}>
        <ProfileAvatar user={userProfile} />
        <Typography variant='body1' sx={{ ml: 1 }}>{label}</Typography>
      </li>
    );
  }, [getDisplayName]);

  const filterOptions = useCallback((options) => options.filter(
    (o) => o.email.includes(inputValue),
  ), [inputValue]);

  return (
    <Autocomplete
      fullWidth
      noOptionsText={noOptionsText}
      renderOption={displayUser}
      getOptionLabel={getDisplayName}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option, currentValue) => option.userId === currentValue.userId}
      options={filledInput ? autocomplete : []}
      autoComplete
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      value={input.value}
      onChange={(e, v) => {
        if (v === null) {
          // default to the current user; backend will set undefined to current user anyway
          input.onChange(user);
        } else {
          input.onChange(v);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position='start' sx={{ padding: 0.5 }}>
                <ProfileAvatar user={input.value} />
              </InputAdornment>),
          }}
        />
      )}
    />
  );
}

export default AuthorAutocompleteField;
